import {
  type BrowserConsoleLogEntry,
  type BrowserDisplaySession,
  type BrowserDisplayIceCandidate,
  type BrowserSessionSnapshot,
  type BrowserStreamEvent,
  type BrowserViewport,
  type ScopedThreadRef,
} from "@cmmd-forge/contracts";
import { formatBrowserErrorForUi } from "@cmmd-forge/shared/browserErrors";
import {
  resolveBrowserFrameRecovery,
  scopedThreadKey,
  type BrowserFrameRecoveryResult,
  type BrowserFrameRecoveryState,
} from "@cmmd-forge/client-runtime";
import { create } from "zustand";

export interface BrowserThreadState {
  session: BrowserSessionSnapshot | null;
  selectedBrowserId: string | null;
  sessionsByBrowserId: Record<string, BrowserSessionSnapshot>;
  lastActivityByTabId: Record<
    string,
    {
      readonly actor: "agent" | "human";
      readonly agentId: string | null;
      readonly browserId: string;
      readonly eventType: BrowserStreamEvent["type"];
      readonly commandType?: string | undefined;
      readonly message?: string | undefined;
      readonly createdAt: string;
    }
  >;
  lastSnapshotContentByTabId: Record<string, string>;
  lastScreenshotDataUrlByTabId: Record<string, string>;
  lastScreenshotMetaByTabId: Record<
    string,
    {
      readonly captureId: string;
      readonly capturedAt: string;
      readonly url: string;
      readonly viewport: BrowserViewport;
    }
  >;
  lastConsoleLogsByTabId: Record<string, ReadonlyArray<BrowserConsoleLogEntry>>;
  lastConsoleLogsMetaByTabId: Record<
    string,
    {
      readonly capturedAt: string;
      readonly tabId: string;
      readonly count: number;
    }
  >;
  lastFrameByTabId: Record<string, string>;
  lastFrameSequenceByTabId: Record<string, number>;
  lastObservedFrameSequenceByTabId: Record<string, number>;
  lastFrameStreamIdByTabId: Record<string, string>;
  lastFrameStreamGenerationByTabId: Record<string, number>;
  frameGapCountByTabId: Record<string, number>;
  lastFrameMetaByTabId: Record<
    string,
    {
      readonly capturedAt: string;
      readonly url: string;
      readonly viewport: BrowserViewport;
    }
  >;
  displayIceCandidatesByClientId: Record<string, ReadonlyArray<BrowserDisplayIceCandidate | null>>;
  lastError: string | null;
}

const EMPTY_BROWSER_THREAD_STATE: BrowserThreadState = {
  session: null,
  selectedBrowserId: null,
  sessionsByBrowserId: {},
  lastActivityByTabId: {},
  lastSnapshotContentByTabId: {},
  lastScreenshotDataUrlByTabId: {},
  lastScreenshotMetaByTabId: {},
  lastConsoleLogsByTabId: {},
  lastConsoleLogsMetaByTabId: {},
  lastFrameByTabId: {},
  lastFrameSequenceByTabId: {},
  lastObservedFrameSequenceByTabId: {},
  lastFrameStreamIdByTabId: {},
  lastFrameStreamGenerationByTabId: {},
  frameGapCountByTabId: {},
  lastFrameMetaByTabId: {},
  displayIceCandidatesByClientId: {},
  lastError: null,
};

function browserThreadKey(threadRef: ScopedThreadRef): string {
  return scopedThreadKey(threadRef);
}

function browserDisplayIsClientOwnedRemoteWebRtcAttach(display: BrowserDisplaySession): boolean {
  const isReconnectingNetworkFailure =
    display.status === "reconnecting" && display.reason === "network_unreachable";
  return (
    display.transport === "remote-webrtc" &&
    (display.status === "connected" ||
      display.status === "connecting" ||
      display.status === "reconnecting") &&
    (display.reason !== "network_unreachable" || isReconnectingNetworkFailure) &&
    display.reason !== "unsupported_host"
  );
}

function browserDisplaySnapshotIsAttachPlaceholder(display: BrowserDisplaySession): boolean {
  if (display.transport !== "remote-webrtc") {
    return false;
  }
  if (display.status === "failed" || display.status === "unsupported") {
    return false;
  }
  return display.reason !== "network_unreachable" && display.reason !== "unsupported_host";
}

function mergeConnectedClientDisplay(input: {
  readonly currentSession: BrowserSessionSnapshot | null;
  readonly nextSession: BrowserSessionSnapshot;
}): BrowserSessionSnapshot {
  const { currentSession, nextSession } = input;
  if (
    !currentSession ||
    currentSession.browserId !== nextSession.browserId ||
    currentSession.sessionId !== nextSession.sessionId ||
    !browserDisplayIsClientOwnedRemoteWebRtcAttach(currentSession.display) ||
    !browserDisplaySnapshotIsAttachPlaceholder(nextSession.display)
  ) {
    return nextSession;
  }

  return {
    ...nextSession,
    display: {
      ...currentSession.display,
      viewport:
        nextSession.display.viewport ?? nextSession.viewport ?? currentSession.display.viewport,
      devicePixelRatio:
        nextSession.display.devicePixelRatio ?? currentSession.display.devicePixelRatio,
      iceServers: nextSession.display.iceServers ?? currentSession.display.iceServers,
      reconnect: currentSession.display.reconnect ?? nextSession.display.reconnect,
    },
  };
}

/**
 * Session snapshots must apply monotonically per session: events can arrive out of
 * order across the WS hop, so a late pre-navigation `session.started` must not
 * regress a settled `session.updated`. Equal timestamps apply (last write wins).
 */
function isStaleBrowserSessionSnapshot(input: {
  readonly storedSession: BrowserSessionSnapshot | null | undefined;
  readonly nextSession: BrowserSessionSnapshot;
}): boolean {
  const { storedSession, nextSession } = input;
  if (
    !storedSession ||
    storedSession.browserId !== nextSession.browserId ||
    storedSession.sessionId !== nextSession.sessionId
  ) {
    return false;
  }
  // Scope the guard to the exact regression it exists for: a LATE
  // pre-navigation session.started (deferred-nav contract) arriving after the
  // SAME session already settled its navigation. Both sides must show the
  // deferred-nav shape — sessions without navigation state (every other
  // lifecycle flow) always apply; a fresh session.started is a legitimate
  // lifecycle reset.
  const nextIsPreNavigation = nextSession.tabs.some((tab) => tab.navigationStatus === "pending");
  const storedIsSettled = storedSession.tabs.some(
    (tab) => tab.navigationStatus === "complete" || tab.navigationStatus === "failed",
  );
  if (!nextIsPreNavigation || !storedIsSettled) {
    return false;
  }
  const storedTime = Date.parse(storedSession.updatedAt);
  const nextTime = Date.parse(nextSession.updatedAt);
  if (Number.isNaN(storedTime) || Number.isNaN(nextTime)) {
    return false;
  }
  return nextTime < storedTime;
}

function upsertThreadState(
  stateByThreadKey: Record<string, BrowserThreadState>,
  threadKey: string,
  next: BrowserThreadState,
) {
  if (stateByThreadKey[threadKey] === next) {
    return stateByThreadKey;
  }
  return {
    ...stateByThreadKey,
    [threadKey]: next,
  };
}

function activityTabId(event: BrowserStreamEvent): string | null {
  switch (event.type) {
    case "tab.updated":
      return event.payload.tab.tabId;
    case "tab.closed":
    case "snapshot.ready":
    case "screenshot.ready":
    case "frame.ready":
    case "console.logs.ready":
    case "display.iceCandidate":
    case "display.metrics":
      return event.payload.tabId;
    case "action.failed":
      return event.payload.tabId ?? null;
    case "display.updated":
    case "session.started":
    case "session.updated":
    case "session.closed":
      return null;
  }
}

function withBrowserActivity(
  current: BrowserThreadState,
  event: BrowserStreamEvent,
): BrowserThreadState {
  const tabId = activityTabId(event);
  if (!tabId) {
    return current;
  }
  return {
    ...current,
    lastActivityByTabId: {
      ...current.lastActivityByTabId,
      [tabId]: {
        actor: event.agentId ? "agent" : "human",
        agentId: event.agentId,
        browserId: event.browserId,
        eventType: event.type,
        ...(event.type === "action.failed" ? { commandType: event.payload.commandType } : {}),
        ...(event.type === "action.failed" ? { message: event.payload.message } : {}),
        createdAt: event.createdAt,
      },
    },
  };
}

function shouldAutoRevealBrowserEvent(event: BrowserStreamEvent): boolean {
  switch (event.type) {
    case "session.started":
    case "session.updated":
    case "tab.updated":
    case "snapshot.ready":
    case "screenshot.ready":
    case "frame.ready":
    case "display.updated":
      return true;
    case "session.closed":
    case "tab.closed":
    case "console.logs.ready":
    case "action.failed":
    case "display.iceCandidate":
    case "display.metrics":
      return false;
  }
}

function browserDisplayIceCandidateFingerprint(
  candidate: BrowserDisplayIceCandidate | null,
): string {
  if (candidate === null) {
    return "end-of-candidates";
  }
  return JSON.stringify([
    candidate.candidate,
    candidate.sdpMid ?? null,
    candidate.sdpMLineIndex ?? null,
    candidate.usernameFragment ?? null,
  ]);
}

function appendDisplayIceCandidate(
  candidates: ReadonlyArray<BrowserDisplayIceCandidate | null>,
  candidate: BrowserDisplayIceCandidate | null,
): ReadonlyArray<BrowserDisplayIceCandidate | null> {
  const fingerprint = browserDisplayIceCandidateFingerprint(candidate);
  return candidates.some(
    (existing) => browserDisplayIceCandidateFingerprint(existing) === fingerprint,
  )
    ? candidates
    : [...candidates, candidate];
}

function browserSessionClosedMessage(event: BrowserStreamEvent): string | null {
  if (event.type !== "session.closed") {
    return null;
  }
  switch (event.payload.reason) {
    case "idle_timeout":
      return "The browser was closed after being idle. Reopen the preview to restore this thread's browser.";
    case "runtime_shutdown":
      return "The browser runtime shut down. Reopen the preview to restore this thread's browser.";
    case "thread_archived":
    case "clear_browsing_data":
    case "user_closed":
    case undefined:
      return null;
  }
}

function nextDisplayLastError(input: {
  readonly currentLastError: string | null;
  readonly event: BrowserStreamEvent & { readonly type: "display.updated" };
}): string | null {
  const display = input.event.payload.display;
  if (display.status === "failed" || display.status === "unsupported") {
    return display.detail ?? "Browser display is unavailable.";
  }
  if (input.currentLastError && /browser display/i.test(input.currentLastError)) {
    return null;
  }
  return input.currentLastError;
}

interface BrowserStateStore {
  browserStateByThreadKey: Record<string, BrowserThreadState>;
  /** Thread keys whose browser session just started — consumed by ChatView to auto-reveal the preview panel. */
  pendingAutoRevealKeys: Set<string>;
  setSessionSnapshot: (threadRef: ScopedThreadRef, session: BrowserSessionSnapshot | null) => void;
  selectBrowserSession: (threadRef: ScopedThreadRef, browserId: string) => void;
  applyBrowserEvent: (threadRef: ScopedThreadRef, event: BrowserStreamEvent) => void;
  clearBrowserState: (threadRef: ScopedThreadRef) => void;
  consumeAutoReveal: (threadKey: string) => void;
}

type BrowserFrameReadyEvent = Extract<BrowserStreamEvent, { readonly type: "frame.ready" }>;

function browserFrameMatchesSession(
  current: BrowserThreadState,
  event: BrowserFrameReadyEvent,
): boolean {
  const eventSession =
    current.sessionsByBrowserId[event.browserId] ??
    (current.session?.browserId === event.browserId ? current.session : null);
  if (!eventSession) {
    return true;
  }
  const matchesSessionId = eventSession.sessionId === event.payload.sessionId;
  const matchesEpoch =
    !event.sessionEpoch ||
    !eventSession.sessionEpoch ||
    event.sessionEpoch === eventSession.sessionEpoch;
  return matchesSessionId && matchesEpoch;
}

interface BrowserFrameOrderingContext {
  readonly decision: BrowserFrameRecoveryResult;
  readonly observedSequence: number;
  readonly frameGapCount: number;
}

function previousBrowserFrameOrdering(
  current: BrowserThreadState,
  tabId: string,
): BrowserFrameRecoveryState {
  const sequence = current.lastFrameSequenceByTabId[tabId] ?? 0;
  return {
    acceptedSequence: sequence,
    observedSequence: current.lastObservedFrameSequenceByTabId[tabId] ?? sequence,
    ...(current.lastFrameStreamIdByTabId[tabId]
      ? { streamId: current.lastFrameStreamIdByTabId[tabId] }
      : {}),
    ...(current.lastFrameStreamGenerationByTabId[tabId] !== undefined
      ? { streamGeneration: current.lastFrameStreamGenerationByTabId[tabId] }
      : {}),
    ...(current.lastFrameMetaByTabId[tabId]?.capturedAt
      ? { acceptedCapturedAt: current.lastFrameMetaByTabId[tabId].capturedAt }
      : {}),
    gapCount: current.frameGapCountByTabId[tabId] ?? 0,
  };
}

function browserFrameOrderingContext(
  current: BrowserThreadState,
  event: BrowserFrameReadyEvent,
): BrowserFrameOrderingContext {
  const tabId = event.payload.tabId;
  const previous = previousBrowserFrameOrdering(current, tabId);
  const decision = resolveBrowserFrameRecovery(previous, {
    sequence: event.payload.sequence,
    capturedAt: event.payload.capturedAt,
    ...(event.payload.streamId ? { streamId: event.payload.streamId } : {}),
    ...(event.payload.streamGeneration !== undefined
      ? { streamGeneration: event.payload.streamGeneration }
      : {}),
    ...(event.payload.keyframe !== undefined ? { keyframe: event.payload.keyframe } : {}),
  });
  return {
    decision,
    observedSequence: decision.state.observedSequence,
    frameGapCount: decision.state.gapCount,
  };
}

function observedBrowserFrameState(
  current: BrowserThreadState,
  event: BrowserFrameReadyEvent,
  ordering: BrowserFrameOrderingContext,
): BrowserThreadState {
  const tabId = event.payload.tabId;
  return {
    ...current,
    lastObservedFrameSequenceByTabId: {
      ...current.lastObservedFrameSequenceByTabId,
      [tabId]: ordering.observedSequence,
    },
    frameGapCountByTabId: {
      ...current.frameGapCountByTabId,
      [tabId]: ordering.frameGapCount,
    },
  };
}

function acceptedBrowserFrameState(
  current: BrowserThreadState,
  event: BrowserFrameReadyEvent,
): BrowserThreadState {
  const tabId = event.payload.tabId;
  return {
    ...current,
    lastFrameByTabId: { ...current.lastFrameByTabId, [tabId]: event.payload.imageDataUrl },
    lastFrameSequenceByTabId: {
      ...current.lastFrameSequenceByTabId,
      [tabId]: event.payload.sequence,
    },
    lastFrameStreamIdByTabId: event.payload.streamId
      ? { ...current.lastFrameStreamIdByTabId, [tabId]: event.payload.streamId }
      : current.lastFrameStreamIdByTabId,
    lastFrameStreamGenerationByTabId:
      event.payload.streamGeneration !== undefined
        ? {
            ...current.lastFrameStreamGenerationByTabId,
            [tabId]: event.payload.streamGeneration,
          }
        : current.lastFrameStreamGenerationByTabId,
    lastFrameMetaByTabId: {
      ...current.lastFrameMetaByTabId,
      [tabId]: {
        capturedAt: event.payload.capturedAt,
        url: event.payload.url,
        viewport: event.payload.viewport,
      },
    },
    lastError: null,
  };
}

function applyBrowserFrameEvent(input: {
  readonly state: BrowserStateStore;
  readonly current: BrowserThreadState;
  readonly event: BrowserFrameReadyEvent;
  readonly threadKey: string;
  readonly pendingAutoRevealKeys: Set<string>;
}):
  | BrowserStateStore
  | Pick<BrowserStateStore, "browserStateByThreadKey" | "pendingAutoRevealKeys"> {
  const { current, event, pendingAutoRevealKeys, state, threadKey } = input;
  if (!browserFrameMatchesSession(current, event)) {
    return state;
  }
  const ordering = browserFrameOrderingContext(current, event);
  if (!ordering.decision.observe) {
    return state;
  }
  const observedState = observedBrowserFrameState(current, event, ordering);
  const nextThreadState = ordering.decision.accept
    ? acceptedBrowserFrameState(observedState, event)
    : observedState;
  return {
    browserStateByThreadKey: upsertThreadState(
      state.browserStateByThreadKey,
      threadKey,
      nextThreadState,
    ),
    pendingAutoRevealKeys,
  };
}

export const useBrowserStateStore = create<BrowserStateStore>((set) => ({
  browserStateByThreadKey: {},
  pendingAutoRevealKeys: new Set<string>(),
  setSessionSnapshot: (threadRef, session) =>
    set((state) => {
      const threadKey = browserThreadKey(threadRef);
      const current = state.browserStateByThreadKey[threadKey] ?? EMPTY_BROWSER_THREAD_STATE;
      const nextSession = session
        ? mergeConnectedClientDisplay({
            currentSession: current.sessionsByBrowserId[session.browserId] ?? current.session,
            nextSession: session,
          })
        : null;
      return {
        browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
          ...current,
          session: nextSession,
          selectedBrowserId: nextSession?.browserId ?? current.selectedBrowserId,
          sessionsByBrowserId: nextSession
            ? {
                ...current.sessionsByBrowserId,
                [nextSession.browserId]: nextSession,
              }
            : current.sessionsByBrowserId,
          lastError: nextSession ? null : current.lastError,
        }),
      };
    }),
  selectBrowserSession: (threadRef, browserId) =>
    set((state) => {
      const threadKey = browserThreadKey(threadRef);
      const current = state.browserStateByThreadKey[threadKey] ?? EMPTY_BROWSER_THREAD_STATE;
      const selected = current.sessionsByBrowserId[browserId] ?? current.session;
      return {
        browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
          ...current,
          selectedBrowserId: browserId,
          session: selected,
        }),
      };
    }),
  applyBrowserEvent: (threadRef, event) =>
    set((state) => {
      const threadKey = browserThreadKey(threadRef);
      const pendingAutoRevealKeys = shouldAutoRevealBrowserEvent(event)
        ? new Set(state.pendingAutoRevealKeys).add(threadKey)
        : state.pendingAutoRevealKeys;
      const current = withBrowserActivity(
        state.browserStateByThreadKey[threadKey] ?? EMPTY_BROWSER_THREAD_STATE,
        event,
      );
      switch (event.type) {
        case "session.started": {
          if (
            isStaleBrowserSessionSnapshot({
              storedSession:
                current.sessionsByBrowserId[event.payload.snapshot.browserId] ?? current.session,
              nextSession: event.payload.snapshot,
            })
          ) {
            return state;
          }
          const nextSnapshot = mergeConnectedClientDisplay({
            currentSession:
              current.sessionsByBrowserId[event.payload.snapshot.browserId] ?? current.session,
            nextSession: event.payload.snapshot,
          });
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session: nextSnapshot,
              selectedBrowserId: current.selectedBrowserId ?? nextSnapshot.browserId,
              sessionsByBrowserId: {
                ...current.sessionsByBrowserId,
                [nextSnapshot.browserId]: nextSnapshot,
              },
              lastError: null,
            }),
            pendingAutoRevealKeys,
          };
        }
        case "session.updated": {
          // session.updated always applies (last write wins): the regression
          // class is a LATE pre-navigation session.started clobbering settled
          // state, which the started-handler guard above covers. Guarding
          // updated events on wall-clock updatedAt over-drops legitimate
          // snapshots when timestamps tie or cross sources.
          const nextSnapshot = mergeConnectedClientDisplay({
            currentSession:
              current.sessionsByBrowserId[event.payload.snapshot.browserId] ?? current.session,
            nextSession: event.payload.snapshot,
          });
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session:
                current.selectedBrowserId &&
                current.selectedBrowserId !== event.payload.snapshot.browserId
                  ? current.session
                  : nextSnapshot,
              selectedBrowserId: current.selectedBrowserId ?? event.payload.snapshot.browserId,
              sessionsByBrowserId: {
                ...current.sessionsByBrowserId,
                [nextSnapshot.browserId]: nextSnapshot,
              },
              lastError: null,
            }),
            pendingAutoRevealKeys,
          };
        }
        case "session.closed":
          const closedSelected =
            current.selectedBrowserId === event.browserId ||
            current.session?.browserId === event.browserId;
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session: closedSelected ? null : current.session,
              selectedBrowserId: closedSelected ? null : current.selectedBrowserId,
              sessionsByBrowserId: Object.fromEntries(
                Object.entries(current.sessionsByBrowserId).filter(
                  ([browserId]) => browserId !== event.browserId,
                ),
              ),
              lastError: browserSessionClosedMessage(event),
            }),
          };
        case "display.updated": {
          const session = current.session;
          if (!session || session.sessionId !== event.payload.sessionId) {
            return {
              browserStateByThreadKey: upsertThreadState(
                state.browserStateByThreadKey,
                threadKey,
                current,
              ),
              pendingAutoRevealKeys,
            };
          }
          const nextSession = {
            ...session,
            display: event.payload.display,
            updatedAt: event.createdAt,
          };
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session: nextSession,
              sessionsByBrowserId: {
                ...current.sessionsByBrowserId,
                [nextSession.browserId]: nextSession,
              },
              lastError: nextDisplayLastError({ currentLastError: current.lastError, event }),
            }),
            pendingAutoRevealKeys,
          };
        }
        case "tab.updated": {
          const session = current.session;
          if (!session || session.sessionId !== event.payload.sessionId) {
            return {
              browserStateByThreadKey: upsertThreadState(
                state.browserStateByThreadKey,
                threadKey,
                current,
              ),
              pendingAutoRevealKeys,
            };
          }
          const nextTabs = session.tabs
            .map((tab) =>
              tab.tabId === event.payload.tab.tabId
                ? {
                    ...event.payload.tab,
                    isActive: tab.tabId === session.activeTabId,
                  }
                : {
                    ...tab,
                    isActive: tab.tabId === session.activeTabId,
                  },
            )
            .concat(
              session.tabs.some((tab) => tab.tabId === event.payload.tab.tabId)
                ? []
                : [
                    {
                      ...event.payload.tab,
                      isActive: event.payload.tab.tabId === session.activeTabId,
                    },
                  ],
            );
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session: {
                ...session,
                tabs: nextTabs,
              },
              lastError: null,
            }),
            pendingAutoRevealKeys,
          };
        }
        case "tab.closed": {
          const session = current.session;
          if (!session || session.sessionId !== event.payload.sessionId) {
            return state;
          }
          const nextTabs = session.tabs.filter((tab) => tab.tabId !== event.payload.tabId);
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              session: {
                ...session,
                tabs: nextTabs,
                activeTabId:
                  session.activeTabId === event.payload.tabId
                    ? (nextTabs[0]?.tabId ?? null)
                    : session.activeTabId,
              },
            }),
          };
        }
        case "snapshot.ready":
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              lastSnapshotContentByTabId: {
                ...current.lastSnapshotContentByTabId,
                [event.payload.tabId]: event.payload.content,
              },
              lastError: null,
            }),
            pendingAutoRevealKeys,
          };
        case "screenshot.ready":
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              lastScreenshotDataUrlByTabId: {
                ...current.lastScreenshotDataUrlByTabId,
                [event.payload.tabId]: event.payload.imageDataUrl,
              },
              lastScreenshotMetaByTabId: {
                ...current.lastScreenshotMetaByTabId,
                [event.payload.tabId]: {
                  captureId: event.payload.captureId,
                  capturedAt: event.payload.capturedAt,
                  url: event.payload.url,
                  viewport: event.payload.viewport,
                },
              },
              lastError: null,
            }),
            pendingAutoRevealKeys,
          };
        case "frame.ready": {
          return applyBrowserFrameEvent({
            state,
            current,
            event,
            threadKey,
            pendingAutoRevealKeys,
          });
        }
        case "console.logs.ready":
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              lastConsoleLogsByTabId: {
                ...current.lastConsoleLogsByTabId,
                [event.payload.tabId]: event.payload.logs,
              },
              lastConsoleLogsMetaByTabId: {
                ...current.lastConsoleLogsMetaByTabId,
                [event.payload.tabId]: {
                  capturedAt: event.createdAt,
                  tabId: event.payload.tabId,
                  count: event.payload.logs.length,
                },
              },
              lastError: null,
            }),
          };
        case "display.iceCandidate": {
          const candidates = current.displayIceCandidatesByClientId[event.payload.clientId] ?? [];
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              displayIceCandidatesByClientId: {
                ...current.displayIceCandidatesByClientId,
                [event.payload.clientId]: appendDisplayIceCandidate(
                  candidates,
                  event.payload.candidate,
                ),
              },
            }),
            pendingAutoRevealKeys,
          };
        }
        case "display.metrics":
          return {
            browserStateByThreadKey: upsertThreadState(
              state.browserStateByThreadKey,
              threadKey,
              current,
            ),
            pendingAutoRevealKeys,
          };
        case "action.failed":
          return {
            browserStateByThreadKey: upsertThreadState(state.browserStateByThreadKey, threadKey, {
              ...current,
              lastError: formatBrowserErrorForUi(event.payload.message),
            }),
          };
        default:
          return state;
      }
    }),
  clearBrowserState: (threadRef) =>
    set((state) => {
      const threadKey = browserThreadKey(threadRef);
      if (!(threadKey in state.browserStateByThreadKey)) {
        return state;
      }
      const { [threadKey]: _removed, ...remaining } = state.browserStateByThreadKey;
      return {
        browserStateByThreadKey: remaining,
      };
    }),
  consumeAutoReveal: (threadKey) =>
    set((state) => {
      if (!state.pendingAutoRevealKeys.has(threadKey)) return state;
      const next = new Set(state.pendingAutoRevealKeys);
      next.delete(threadKey);
      return { pendingAutoRevealKeys: next };
    }),
}));

export function selectBrowserThreadState(
  browserStateByThreadKey: Record<string, BrowserThreadState>,
  threadRef: ScopedThreadRef | null | undefined,
): BrowserThreadState | null {
  if (!threadRef) {
    return null;
  }
  return browserStateByThreadKey[browserThreadKey(threadRef)] ?? null;
}
